import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, MailWarning } from 'lucide-react';
import { api } from '../../services/api';
import C from './constants/colors.js';

type Durum = 'bekliyor' | 'basarili' | 'suresiDoldu' | 'gecersiz';

/**
 * E-posta doğrulama bağlantısının açtığı sayfa: /verify-email?token=...
 *
 * Jeton tek kullanımlıktır, bu yüzden istek yalnızca bir kez gönderilir —
 * React'in StrictMode'da efektleri iki kez çalıştırması ikinci istekte
 * "geçersiz jeton" göstermesine yol açardı.
 */
export function EduAnalizVerifyEmail() {
    const [durum, setDurum] = useState<Durum>('bekliyor');
    const [mesaj, setMesaj] = useState('');
    const [email, setEmail] = useState('');
    const [yenidenGonderildi, setYenidenGonderildi] = useState(false);
    const [gonderiliyor, setGonderiliyor] = useState(false);
    const istekGonderildi = useRef(false);

    useEffect(() => {
        if (istekGonderildi.current) return;
        istekGonderildi.current = true;

        const token = new URLSearchParams(window.location.search).get('token') || '';
        if (!token) {
            setDurum('gecersiz');
            setMesaj('Doğrulama bağlantısı eksik görünüyor.');
            return;
        }

        api.verifyEduAnalizEmail(token)
            .then((data) => {
                setDurum('basarili');
                setEmail(data.email || '');
            })
            .catch((err) => {
                const metin = String(err?.message || '');
                setDurum(metin.includes('süresi doldu') ? 'suresiDoldu' : 'gecersiz');
                setMesaj(metin || 'Doğrulama tamamlanamadı.');
            });
    }, []);

    const yenidenGonder = async () => {
        if (!email.trim()) return;
        setGonderiliyor(true);
        try {
            await api.resendEduAnalizVerification(email.trim());
            setYenidenGonderildi(true);
        } catch {
            setYenidenGonderildi(true); // Uç zaten adres ayrımı yapmadan aynı yanıtı veriyor
        } finally {
            setGonderiliyor(false);
        }
    };

    const kart: React.CSSProperties = {
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 24,
        padding: 36, width: '100%', maxWidth: 460, textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
    };
    const daire = (renk: string): React.CSSProperties => ({
        width: 64, height: 64, borderRadius: 18, background: `${renk}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
    });
    const girdi: React.CSSProperties = {
        width: '100%', padding: '12px 14px', borderRadius: 10,
        border: `1px solid ${C.border}`, background: C.bg, color: C.text,
        fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10,
    };

    return (
        <div style={{
            minHeight: '100vh', background: C.bg, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
            <div style={kart}>
                {durum === 'bekliyor' && (
                    <>
                        <div style={daire(C.accent)}>
                            <Loader2 size={28} color={C.accent} style={{ animation: 'spin 1s linear infinite' }} />
                        </div>
                        <h1 style={{ fontSize: 19, fontWeight: 900, color: C.text, marginBottom: 8 }}>
                            E-posta adresiniz doğrulanıyor
                        </h1>
                        <p style={{ fontSize: 14, color: C.muted }}>Lütfen bekleyin...</p>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </>
                )}

                {durum === 'basarili' && (
                    <>
                        <div style={daire(C.accent2)}>
                            <CheckCircle2 size={30} color={C.accent2} />
                        </div>
                        <h1 style={{ fontSize: 19, fontWeight: 900, color: C.text, marginBottom: 8 }}>
                            E-posta adresiniz doğrulandı
                        </h1>
                        <p style={{ fontSize: 14, color: C.muted, marginBottom: 26, lineHeight: 1.6 }}>
                            {email && <strong style={{ color: C.text }}>{email}</strong>}
                            {email ? ' adresi onaylandı. ' : ''}
                            Artık hesabınıza giriş yapabilirsiniz.
                        </p>
                        <a href="/eduanaliz" style={{
                            display: 'inline-block', padding: '13px 30px', background: C.accent,
                            color: '#fff', textDecoration: 'none', borderRadius: 12, fontWeight: 800, fontSize: 14,
                        }}>
                            Giriş Yap
                        </a>
                    </>
                )}

                {(durum === 'suresiDoldu' || durum === 'gecersiz') && (
                    <>
                        <div style={daire(durum === 'suresiDoldu' ? C.accent3 : C.danger)}>
                            {durum === 'suresiDoldu'
                                ? <MailWarning size={30} color={C.accent3} />
                                : <XCircle size={30} color={C.danger} />}
                        </div>
                        <h1 style={{ fontSize: 19, fontWeight: 900, color: C.text, marginBottom: 8 }}>
                            {durum === 'suresiDoldu' ? 'Bağlantının süresi doldu' : 'Bağlantı geçersiz'}
                        </h1>
                        <p style={{ fontSize: 14, color: C.muted, marginBottom: 22, lineHeight: 1.6 }}>
                            {mesaj || 'Bu bağlantı kullanılamıyor.'}
                        </p>

                        {yenidenGonderildi ? (
                            <div style={{
                                background: `${C.accent2}12`, border: `1px solid ${C.accent2}44`,
                                borderRadius: 12, padding: '14px 16px', fontSize: 13,
                                color: C.accent2, fontWeight: 700, marginBottom: 18,
                            }}>
                                Hesabınız doğrulama bekliyorsa yeni bir bağlantı gönderildi.
                                Gelen kutunuzu kontrol edin.
                            </div>
                        ) : (
                            <>
                                <p style={{ fontSize: 13, color: C.text, fontWeight: 700, marginBottom: 10, textAlign: 'left' }}>
                                    Yeni bağlantı isteyin
                                </p>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && yenidenGonder()}
                                    placeholder="E-posta adresiniz"
                                    style={girdi}
                                />
                                <button
                                    onClick={yenidenGonder}
                                    disabled={!email.trim() || gonderiliyor}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: 12, border: 'none',
                                        background: (!email.trim() || gonderiliyor) ? C.border : C.accent,
                                        color: '#fff', fontWeight: 800, fontSize: 14,
                                        cursor: (!email.trim() || gonderiliyor) ? 'default' : 'pointer',
                                        marginBottom: 18,
                                    }}>
                                    {gonderiliyor ? 'Gönderiliyor...' : 'Bağlantıyı Yeniden Gönder'}
                                </button>
                            </>
                        )}

                        <a href="/eduanaliz" style={{ fontSize: 13, color: C.muted, fontWeight: 700 }}>
                            Giriş sayfasına dön
                        </a>
                    </>
                )}
            </div>
        </div>
    );
}

export default EduAnalizVerifyEmail;
